import { GBadge } from 'components/basic-blocks/g-badge'
import { GCheckbox } from 'components/basic-blocks/g-checkbox'
import { GTextArea } from 'components/basic-blocks/g-text-area'
import { useStores } from 'hooks'
import { join } from 'lodash'
import { classNames } from 'utils'
import { ProtectionChecksProps } from './types'
import { ChangeEvent } from 'react'

const options = [
	{
		label: 'Geolocation',
		value: 'geo',
		text: 'Detect attacks outside advertising region'
	},
	{
		label: 'Placements',
		value: 'placements',
		text: 'Prevent fraudulent placements from showing your display ads',
		feature: 'protection_placements',
		feature_tier: 'pro'
	},
	{
		label: 'Bounced clicks',
		value: 'bounced',
		text: 'Detect visitors that frequently bounce'
	},
	{
		label: 'Proxies',
		value: 'proxy',
		text: 'Detect traffic from known proxies and VPNs'
	},
	{
		label: 'Bots',
		value: 'bots',
		text: 'Detect bots and other non-human traffic',
		feature: 'protection_bots',
		feature_tier: 'standard'
	},
	{
		label: 'IP ranges',
		value: 'ip_range',
		text: 'Detect visitors coming from the same IP address range',
		feature: 'protection_iprange',
		feature_tier: 'standard'
	},
	{
		label: 'Multi-campaign',
		value: 'multi_target',
		text: 'Detect visitors targeting multiple campaigns',
		feature: 'protection_multitarget',
		feature_tier: 'standard'
	},
	{
		label: 'Website interactions',
		value: 'interaction',
		text: 'Detect bad visitors based on website engagement',
		feature: 'protection_interaction',
		feature_tier: 'standard'
	},
	{
		label: 'Non-converting',
		value: 'non_converting',
		text: 'Detect excessive non-converting traffic',
		feature: 'protection_conversion',
		feature_tier: 'pro'
	},
	{
		label: 'Conversions',
		value: 'converted',
		text: 'Detect visitors that already converted',
		feature: 'protection_conversion',
		feature_tier: 'pro'
	}
]
const whiteListing = {
	label: 'White listing',
	tooltip: 'Traffic coming from whitelisted IP addresses will not be blocked.',
	text: 'Whitelist selected IP addresses',
	subText: 'IP addresses you want to whitelist, one per line:'
}

export const ProtectionChecks = ({
	checks,
	setProtectionChecksOptions
}: ProtectionChecksProps) => {
	const { accountStore } = useStores()
	const isFeatureDisabled = (feature?: string) => {
		return feature ? !accountStore.hasFeature(feature) : false
	}

	return (
		<div className="flex flex-col gap-6 mt-4">
			{options.map((option) => (
				<div className="flex items-center" key={option.value}>
					<div
						className={classNames(
							'flex items-center',
							isFeatureDisabled(option.feature) ? 'opacity-50' : ''
						)}
					>
						<GCheckbox
							name={option.label}
							label={option.text}
							checked={!!checks[option.value]}
							className="cursor-pointer"
							disabled={isFeatureDisabled(option.feature)}
							onChange={() =>
								setProtectionChecksOptions((prev) => {
									return {
										...prev,
										[option.value]: !checks[option.value]
									}
								})
							}
						/>
					</div>
					{isFeatureDisabled(option.feature) && (
						<>
							{option.feature_tier === 'pro' ? (
								<GBadge
									text="Pro"
									color="purple"
									tooltip="Feature available with Pro subscription"
									className="ml-2"
								/>
							) : (
								<GBadge
									text="Standard"
									color="purple"
									tooltip="Feature available with Standard subscription"
									className="ml-2"
								/>
							)}
						</>
					)}
				</div>
			))}
			<div className="flex">
				<div className="flex flex-col">
					<GCheckbox
						name={whiteListing.label}
						label={whiteListing.text}
						labelClassName="text-color-text-default"
						checked={checks.white_listing}
						onChange={() =>
							setProtectionChecksOptions((prev) => {
								return {
									...prev,
									white_listing: !checks.white_listing
								}
							})
						}
					/>
					{checks.white_listing && (
						<>
							<span className="my-3">{whiteListing.subText}</span>
							<GTextArea
								onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
									setProtectionChecksOptions((prev) => {
										return {
											...prev,
											ip_whitelist: [e.target.value]
										}
									})
								}}
								value={join(checks.ip_whitelist)}
								cols={30}
								rows={4}
								className="font-bold"
							/>
						</>
					)}
				</div>
			</div>
		</div>
	)
}
